import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context/auth';
import { Box, Button, FormControl, FormLabel, Heading, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Textarea, Divider, useToast, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Tag, Center, Spinner } from '@chakra-ui/react';
import { config, mfrProcessSelectables, subSystemSelectables, materialSelectables, finishSelectables, prioritySelectables } from 'util/constants';
import { CreatableSelect, Select } from "chakra-react-select";
import { MCFFormsContext } from 'context/mcfforms';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

export default function SubmitForRerun(props) {
    const { user } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const { MCFForms, getMCFForms } = useContext(MCFFormsContext);
    const { objectId: objectIdParam } = useParams();
    const [authorizedUser, setAuthorizedUser] = useState(false);
    const [form, setForm] = useState(null);
    const navigate = useNavigate();

    const errorToast = () => {
        toast({
            title: 'Error',
            description: 'There was an error processing your request',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);

        let formData = {};
        formData.subsystem = e.target.subsystem.value;
        formData.filePath = e.target.filePath.value;
        formData.fileName = e.target.fileName.value;
        formData.revision = e.target.revision.value;
        formData.quantity = e.target.quantity.value;
        formData.material = e.target.material.value;
        formData.mfrProcess = [];
        // Machine is a multi-select input so need to check if more than 1 is selected
        if (e.target.mfrprocess.length > 1) // If more than 1 is selected then the return value becomes RadioNodeList array
        {
            e.target.mfrprocess.forEach((option) => {
                formData.mfrProcess.push(option.value);
            })
        }
        else {
            formData.mfrProcess.push(e.target.mfrprocess.value);
        }
        formData.finish = e.target.finish.value;
        formData.priority = e.target.priority.value;
        formData.notes = [];
        formData.notes.push({
            timestamp: Date.now(),
            note: "Submitted for Rerun"
        });
        formData.googleId =  user.googleId;
        formData.firstName = user.firstName;
        formData.lastName = user.lastName;
        if (e.target.notes.value)
        {
            formData.notes.push({ 
                timestamp: Date.now(),
                note: `${user.firstName} ${user.lastName.charAt(0)}: ${e.target.notes.value}`
            });
        }

        fetch(`/transactions/submitForRerun`, {
            method: 'POST',
            // We convert the React state to JSON and send it as the POST body
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        }).then(response => {
            if (response.status !== 200) {
                throw `${response.status}: ${response.statusText}`
            }
            return response.text();
        }).then(function(data) {
            // Success
            e.target.reset();
            getMCFForms(() => {
                setTimeout(() => {
                    toast({
                        title: 'MCF Submitted for Rerun',
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                    });
                    setLoading(false);
                    navigate('/');
                }, 500);
            });
        })
        .catch(err => {
            setLoading(false);
            console.log(err);
            errorToast();
        });
        
    }
    
    const handlePriority = (priority) => {
        switch(priority)
        {
            case 1: return 'National Emergency'
            case 2: return 'High'
            case 3: return 'Medium'
            case 4: return 'Low'
            default: 'Undefined'
        }
    }

    useEffect(() => {
        try
        {
            fetch(`/transactions/getMCFForm/${objectIdParam}`, {
                method: 'GET',
            }).then(response => {
                if (response.status !== 200) {
                    throw `${response.status}: ${response.statusText}`
                }
                return response.json();
            }
            ).then(data => {
                data.docs.mfrProcessDefault = data.docs.mfrProcess.map(e => {
                    return { label: e, value: e }
                });

                setTimeout(() => setForm(data.docs), 250);
                if (data.docs.approved && (data.docs.googleId === user.googleId || user.admin))
                {
                    setAuthorizedUser(true);
                }
            }).catch(err => {
                console.error(err);
            });

        } catch (e)
        {
            console.log(e);
        }
    }, []);

    if (!form)
    {
        return (<>
        <Box margin={'0 auto'} mt={"1em"} width={{ base: '85%', md: '66%', lg: '50%' }}>
            <Tag backgroundColor={'white'}>
                <Breadcrumb separator={<ChevronRightIcon color='gray.500' />}>
                    <BreadcrumbItem>
                        <BreadcrumbLink as={RouterLink} to={`/`}>Home</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                        <BreadcrumbLink>Edit Form</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
            </Tag>
            <Center><Spinner mt={'2em'} size='lg' /></Center>
        </Box></>)
    }

    return (
        <>
            <Box margin={'0 auto'} mt={"1em"} width={{ base: '85%', md: '66%', lg: '50%' }}>
                <Tag backgroundColor={'white'}>
                    <Breadcrumb separator={<ChevronRightIcon color='gray.500' />}>
                        <BreadcrumbItem>
                            <BreadcrumbLink as={RouterLink} to={`/`}>Home</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem>
                            <BreadcrumbLink>Submit Rerun</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Tag>
            </Box>
            <Box margin={'1em auto'} width={{ base: '85%', md: '66%', lg: '50%' }}
                p={4}
                borderWidth="1px"
                rounded="lg"
                shadow="1px 1px 3px rgba(0,0,0,0.3)"
                bgColor={'#fff'}>
                {authorizedUser ? (<>
                <Heading mb={2} as="h1" size="lg" textAlign="center">
                    Submit Part MCF for Rerun
                </Heading>
                <Divider />
                <form onSubmit={handleSubmit} autoComplete="off">
                <FormControl isRequired mt={2}>
                        <FormLabel>Subsystem</FormLabel>
                        <Select isReadOnly variant='filled' required name="subsystem" size="sm"
                        defaultValue={{label: form.subsystem, value: form.subsystem}}
                        options={subSystemSelectables}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>File Path</FormLabel>
                        <Input isReadOnly onFocus={e => e.target.blur() } variant='filled' name="filePath" type="text" size="sm" defaultValue={form.filePath}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>File Name</FormLabel>
                        <Input isReadOnly onFocus={e => e.target.blur() } variant='filled' name="fileName" type="text" size="sm" defaultValue={form.fileName}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Revision</FormLabel>
                        <Input isReadOnly onFocus={e => e.target.blur() } variant='filled' name="revision" type="text" size="sm" defaultValue={form.revision}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Quantity</FormLabel>
                        <NumberInput name="quantity" defaultValue={form.quantity} min={1} max={9001}>
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                        </NumberInput>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Material</FormLabel>
                        <CreatableSelect isReadOnly variant='filled' required name="material" placeholder="Select Material..." size="sm"
                        defaultValue={{ label: form.material, value: form.material }}
                        options={materialSelectables}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Machine(s)</FormLabel>
                        <Select isReadOnly variant='filled' required isMulti name="mfrprocess"  size="sm"
                        value={form.mfrProcessDefault}
                        options={mfrProcessSelectables}
                        />
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Finish</FormLabel>
                        <CreatableSelect isReadOnly variant='filled' required name="finish" placeholder="Select Finish..." size="sm"
                        defaultValue={{ label: form.finish, value: form.finish }}
                        options={finishSelectables}/>
                    </FormControl>
                    <FormControl isRequired mt={2}>
                        <FormLabel>Priority</FormLabel>
                        <Select required name="priority" size="sm"
                        defaultValue={{label: handlePriority(form.priority), value: form.priority}}
                        options={prioritySelectables}/>
                    </FormControl>
                    <FormControl mt={2}>
                        <FormLabel>Add Note</FormLabel>
                        <Textarea name="notes"
                            placeholder='Notes'
                            size='md'
                        />
                    </FormControl>
                    <Button
                        isLoading={loading}
                        name="submitButton"
                        loadingText='Submitting'
                        colorScheme="teal"
                        type="submit"
                        width="full"
                        mt="4"
                    >
                        Submit
                    </Button>
                </form>
                </>
                ) : (<><Center>Not Authorizied</Center></>)}
            </Box>
        </>
    );
}
